import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PriceArea } from '../types';
import { getPriceAreaInfo } from '../utils/api';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

// Fallback descriptions for when location isn't available
const DEFAULT_TITLE = "Strømkalkulator | Se strømpriser og strømstøtte";
const DEFAULT_DESCRIPTION = "Sjekk strømprisene time for time og finn de billigste tidene å bruke strøm. Oppdaterte priser ✓ Strømstøtte inkludert ✓ Strømkalkulator ✓ Spar penger på strømregningen.";

interface SEOProps {
  area: PriceArea;
  date: Date;
}

const SEO: React.FC<SEOProps> = ({ area, date }) => {
  const areaInfo = getPriceAreaInfo().find(a => a.id === area);

  // Dynamic title based on area - optimized for SEO
  const title = `Strømpriser ${areaInfo?.description || 'Norge'} i dag | Strømkalkulator og Strømstøtte ${new Date().getFullYear()}`;

  const description = `Se dagens strømpriser i ${areaInfo?.description || 'ditt område'} time for time ⚡ Oppdatert strømpris nå ✓ Strømstøtte kalkulator ${new Date().getFullYear()} ✓ Sammenlign strømleverandører ✓ Billigste timer å bruke strøm ✓ Spar penger på strømregningen! Gratis strømkalkulator.`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={`strømpriser ${new Date().getFullYear()}, strømkalkulator, ${areaInfo?.description.toLowerCase() || 'norge'}, strømstøtte ${new Date().getFullYear()}, spotpris strøm, billig strøm, strømforbruk, strømsparing, strømpris nå, strømpris i dag, strømpris i morgen, strømstøtte beregning, strømpris ${areaInfo?.name.toLowerCase() || 'oslo'}, hvakosterstrommen, dagens strømpris, norgespris, sammenlign strømleverandører, strømavtaler, spotpris strøm, kraftpris, elhub, nordpool`} />
      <meta name="news_keywords" content="strømpriser, strømstøtte, norgespris, strømkalkulator, kraftpris, nordpool" />
      
      {/* Alternate language tags */}
      <link rel="alternate" hrefLang="no" href={window.location.href} />
      <link rel="alternate" hrefLang="x-default" href="https://stromkalkulator.no" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Stromkalkulator.no" />
      <meta property="og:locale" content="nb_NO" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:site" content="@stromkalkulator" />

      {/* Additional SEO meta tags */}
      <meta name="geo.region" content="NO" />
      {areaInfo && <meta name="geo.placename" content={areaInfo.description} />}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="Strømkalkulator.no" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#3b82f6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Strømkalkulator" />
      <meta name="application-name" content="Strømkalkulator" />
      
      {/* Structured data / JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Strømkalkulator.no - Sjekk strømpriser og strømstøtte",
          "description": description,
          "applicationCategory": "UtilityApplication",
          "operatingSystem": "Any",
          "url": "https://stromkalkulator.no",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "NOK"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Strømkalkulator.no",
            "url": "https://stromkalkulator.no",
            "logo": {
              "@type": "ImageObject",
              "url": "https://stromkalkulator.no/logo.png",
              "width": "60",
              "height": "60"
            }
          },
          "datePublished": "2024-01-01T00:00:00+00:00",
          "dateModified": new Date().toISOString(),
          "inLanguage": "nb-NO",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://stromkalkulator.no/?area={area}"
            },
            "query-input": "required name=area"
          }
        })}
      </script>
      
      {/* Add FAQ structured data for rich snippets */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Hvordan fungerer strømstøtten i 2026?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "I 2026 dekker staten 90 % av spotprisen som overstiger 77 øre/kWh (uten mva.), beregnet time for time. Støtten gjelder for forbruk opp til 5000 kWh per måned per målepunkt i boliger. Fritidsboliger får ikke strømstøtte, med mindre noen bor fast der. Strømstøtten trekkes automatisk fra på nettleiefakturaen og krever ingen søknad. Har du Norgespris, får du ikke strømstøtte."
              }
            },
            {
              "@type": "Question",
              "name": "Hva er strømprisen i dag?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Strømprisen i dag varierer time for time og mellom ulike prisområder i Norge. Bruk vår strømkalkulator for å se oppdaterte strømpriser time for time i ditt område. Vi viser spotpriser fra Nord Pool og inkluderer strømstøtte automatisk."
              }
            },
            {
              "@type": "Question",
              "name": "Når er strømmen billigst på døgnet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Strømmen er typisk billigst om natten mellom kl. 00:00-06:00 og midt på dagen mellom kl. 10:00-14:00. Dyreste timer er vanligvis kl. 07:00-09:00 og 17:00-20:00. Bruk vår strømkalkulator for å se nøyaktige priser time for time og finn de billigste timene."
              }
            },
            {
              "@type": "Question",
              "name": "Hvordan kan jeg spare penger på strømregningen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Du sparer penger ved å bruke strøm når prisen er lavest. Flytt strømkrevende aktiviteter som oppvask, klesvask og elbillading til timer med lave priser. Bruk vår strømkalkulator for å finne de billigste timene hver dag. Vi viser også strømstøtte og gir tips for energisparing."
              }
            },
            {
              "@type": "Question",
              "name": "Hva er Norgespris?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Norgespris er en statlig fastpris på 40 øre/kWh uten mva. (50 øre med mva., 40 øre i Nord-Norge) og et alternativ til strømstøtte. Den gjelder for opptil 5000 kWh per måned i boliger og 1000 kWh i fritidsboliger. Prisperioden varer til 31. desember 2026. Alle som vil ha Norgespris i 2027, må bestille på nytt på minside.elhub.no når bestillingen åpner etter at Stortinget har vedtatt statsbudsjettet."
              }
            },
            {
              "@type": "Question",
              "name": "Hvor finner jeg de billigste strømavtalene?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Bruk vår sammenligning av strømleverandører for å finne de billigste strømavtalene. Vi sammenligner priser fra alle leverandører i Norge basert på ditt forbruk og område. Spotprisavtaler er vanligvis billigst når du kombinerer dem med smart strømbruk."
              }
            }
          ]
        })}
      </script>
      
      {/* Organization structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Strømkalkulator.no",
          "url": "https://stromkalkulator.no",
          "logo": "https://stromkalkulator.no/logo.png",
          "description": "Norges ledende strømkalkulator for å sjekke strømpriser og strømstøtte. Få oppdaterte priser time for time og spar penger på strømregningen.",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "NO"
          },
          "sameAs": [
            "https://www.facebook.com/stromkalkulator",
            "https://twitter.com/stromkalkulator"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["Norwegian", "nb-NO"]
          }
        })}
      </script>

      {/* Breadcrumb structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Hjem",
              "item": "https://stromkalkulator.no"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": `Strømpriser ${areaInfo?.description || 'Norge'}`,
              "item": window.location.href
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;